import { useEffect, useRef, useState } from 'react';
import { ExtensionCategory, Graph, register, type NodeData } from '@antv/g6';
import { ReactNode } from '@antv/g6-extension-react';
import { Alert } from 'antd';
import { useTranslation } from 'react-i18next';

import themeToken from '@/lib/theme/tokens';
import { formatNumber } from '@/utils/formatNumber';
import type { IndirectHolding } from '../api/getAllUserShares';

register(ExtensionCategory.NODE, 'react-node', ReactNode);

type Props = {
  holdings: IndirectHolding[];
};

const IndirectShares = ({ holdings }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [renderError, setRenderError] = useState(false);
  const { t } = useTranslation('profile', { keyPrefix: 'shares' });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const measure = () => setWidth(container.clientWidth || 640);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!containerRef.current || !width || !holdings.length) return;
    const nodeWidth = Math.min(224, Math.max(96, width - 32));
    const nodeHeight = 160;
    const companies = new Map<
      string,
      {
        companyName: string;
        ownership: number;
        indirect: boolean;
        intermediary: boolean;
      }
    >();
    const edges = new Map<string, { id: string; source: string; target: string }>();
    for (const holding of holdings) {
      companies.set(`company-${holding.company_id}`, {
        companyName: holding.company_name,
        ownership: holding.indirect_ownership,
        indirect: true,
        intermediary: false,
      });
    }
    for (const holding of holdings) {
      const target = `company-${holding.company_id}`;
      for (const via of holding.via) {
        const source = `company-${via.company_id}`;
        const intermediary = companies.get(source);
        if (intermediary) {
          intermediary.intermediary = true;
        } else {
          companies.set(source, {
            companyName: via.company_name,
            ownership: via.user_ownership,
            indirect: false,
            intermediary: true,
          });
        }
        const id = `${source}-${target}`;
        edges.set(id, {
          id,
          source,
          target,
        });
      }
    }
    const graph = new Graph({
      container: containerRef.current,
      width,
      height: 480,
      autoFit: 'view',
      padding: 24,
      zoomRange: [0.2, 1],
      behaviors: ['drag-canvas', 'zoom-canvas'],
      layout: {
        type: 'dagre',
        rankdir: width < 600 ? 'TB' : 'LR',
        nodeSize: [nodeWidth, nodeHeight],
        nodesep: 40,
        ranksep: 96,
      },
      data: {
        nodes: [...companies.keys()].map((id) => ({ id })),
        edges: [...edges.values()],
      },
      edge: {
        type: 'polyline',
        style: {
          sourcePort: 'ownership-out',
          targetPort: 'ownership-in',
          endArrow: true,
          stroke: themeToken.colorPrimary,
          lineWidth: 2,
          label: false,
          radius: 8,
        },
      },
      node: {
        type: 'react-node',
        style: {
          size: [nodeWidth, nodeHeight],
          dx: -nodeWidth / 2,
          dy: -nodeHeight / 2,
          ports: [
            { key: 'ownership-out', placement: width < 600 ? 'bottom' : 'right' },
            { key: 'ownership-in', placement: width < 600 ? 'top' : 'left' },
          ],
          component: (node: NodeData) => {
            const share = companies.get(node.id);
            if (!share) return null;
            return (
              <div
                style={{
                  width: nodeWidth,
                  height: nodeHeight,
                  padding: 16,
                  boxSizing: 'border-box',
                  background: themeToken.colorPrimary,
                  color: '#fff',
                  borderRadius: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  {share.intermediary && (
                    <div style={{ fontSize: 12, lineHeight: '16px', color: '#bdabff', marginBottom: 4 }}>
                      {t('intermediaryCompany')}
                    </div>
                  )}
                  <div
                    title={share.companyName}
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      lineHeight: '20px',
                      overflowWrap: 'anywhere',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {share.companyName}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, lineHeight: '18px', color: '#bdabff' }}>
                    {t(share.indirect ? 'indirectOwnership' : 'userOwnership')}
                  </div>
                  <div style={{ fontSize: 26, lineHeight: '32px', fontWeight: 600 }}>
                    {formatNumber(share.ownership, { maximumFractionDigits: 2 })} %
                  </div>
                </div>
              </div>
            );
          },
        },
      },
    });
    let active = true;
    setRenderError(false);
    void graph.render().catch(() => {
      if (active) setRenderError(true);
    });
    return () => {
      active = false;
      graph.destroy();
    };
  }, [holdings, width, t]);

  return (
    <div>
      {renderError && <Alert type="error" showIcon message={t('graphError')} />}
      <div style={{ height: 480, overflow: 'auto', contain: 'inline-size' }}>
        <div ref={containerRef} style={{ width: '100%', minHeight: 480 }} />
      </div>
    </div>
  );
};

export default IndirectShares;
